import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { useCompanyConfigQuery } from "api";
import {
  ActionTypes,
  ConfigStatusType,
  useConfig,
} from "contexts/ConfigContext";
import { SourceTypesProvider } from "contexts/SourceTypesContext";
import useFavicon from "hooks/useFavicon";
import useQueryParams from "hooks/useQueryParams";
import { useEffect, useMemo } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";

import CentreOverlay from "components/CentreOverlay";
import ErrorFallback from "components/ErrorFallback";
import { LinkSDK } from "components/LinkSDK";
import Spinner from "components/Spinner";
import { routes } from "config/urls";
import NoMatch from "pages/NoMatch";

import CompanyConsent from "./CompanyConsent";
import CompanyContainer from "./CompanyContainer";
import CompanyError from "./CompanyError";
import CompanyLink from "./CompanyLink";
import CompanyOverview from "./CompanyOverview";
import CompanyRevisit from "./CompanyRevisit";
import CompanySuccess from "./CompanySuccess";
import CustomIntro from "./CustomIntro";
import { PublicApiServiceProvider, LinkApiServiceProvider } from "contexts";

export function Company({ match }: RouteComponentProps<{ companyId: string }>) {
  const queryParams = useQueryParams();
  const analytics = useAnalytics();
  const { companyId } = match.params;

  const { state, dispatch } = useConfig();
  const [{ loading, error, data }, getCompanyConfig] = useCompanyConfigQuery({
    companyId,
    queryParams,
  });

  useFavicon(state?.data?.icon?.src);

  const userProperties = useMemo(
    () => ({
      companyId,
      clientId: data?.clientId ?? state.data.clientId,
      clientName: data?.clientName ?? state.data.clientName,
    }),
    [
      companyId,
      data?.clientId,
      data?.clientName,
      state.data.clientId,
      state.data.clientName,
    ]
  );

  useEffect(() => {
    if (
      userProperties.companyId &&
      userProperties.clientId &&
      userProperties.clientName
    ) {
      analytics.setUserProperties(userProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProperties]);

  useEffect(() => {
    const controller = new AbortController();
    if (state.status === ConfigStatusType.EMPTY) {
      getCompanyConfig({
        signal: controller.signal,
      }).catch((error) => {
        if (controller.signal.aborted) {
          // Do nothing, request was cancelled intentionally
          return;
        }
        throw error;
      });
    }
    return () => controller.abort();
  }, [getCompanyConfig, state.status]);

  useEffect(() => {
    if (data) {
      dispatch({
        type: ActionTypes.SET_CONFIG,
        payload: data,
      });
    }
  }, [dispatch, data]);

  const {
    data: { isNewLinkFlowEnabled },
  } = state;

  if (error) {
    return <ErrorFallback error={error} />;
  }

  if (loading || state.status === ConfigStatusType.EMPTY) {
    return (
      <CentreOverlay data-testid="company-config-loading">
        <Spinner />
      </CentreOverlay>
    );
  }

  return (
    <EventContextProvider data={userProperties}>
      {isNewLinkFlowEnabled ? (
        <PublicApiServiceProvider>
          <LinkApiServiceProvider>
            <LinkSDK companyId={companyId} />
          </LinkApiServiceProvider>
        </PublicApiServiceProvider>
      ) : (
        <SourceTypesProvider companyId={match.params.companyId}>
          <Switch>
            <Route
              exact
              path={routes.company.root}
              component={CompanyContainer}
            />
            <Route exact path={routes.company.link} component={CompanyLink} />
            <Route
              exact
              path={routes.company.customIntro}
              component={CustomIntro}
            />
            <Route
              exact
              path={routes.company.consent}
              component={CompanyConsent}
            />
            <Route
              exact
              path={routes.company.success}
              component={CompanySuccess}
            />
            <Route
              exact
              path={routes.company.overview}
              component={CompanyOverview}
            />
            <Route
              exact
              path={routes.company.revisit}
              component={CompanyRevisit}
            />
            <Route exact path={routes.company.error} component={CompanyError} />
            <Route path="*" component={NoMatch} />
          </Switch>
        </SourceTypesProvider>
      )}
    </EventContextProvider>
  );
}
